/**
 * Firebase app initialisation and the single regioned Functions instance.
 *
 * This module (and the rest of src/lib/backend/) is the ONLY place allowed to import the
 * Firebase SDK surfaces the app talks to. Features go through the callAdmin/callBackend seam
 * (docs/07 §3.2). The Functions instance is ALWAYS regioned to me-central1 - the default
 * instance targets us-central1 (wrong region live) and silently bypasses the emulator (docs/03 §2).
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { firebaseConfig, FUNCTIONS_REGION, useEmulators, emulatorHosts } from './config';

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
// autoDetectLongPolling: some networks (ad-blockers, privacy extensions, corporate proxies) block
// Firestore's default WebChannel stream (seen as net::ERR_BLOCKED_BY_CLIENT), which makes the SDK
// hang ~15-20s before reads resolve. Auto-detect switches to long-polling in those environments so
// reads are fast, while keeping WebChannel where it works.
export const db: Firestore = initializeFirestore(app, { experimentalAutoDetectLongPolling: true });
/** The regioned Functions instance - the ONLY one the seam ever uses. */
export const functions: Functions = getFunctions(app, FUNCTIONS_REGION);
export const storage: FirebaseStorage = getStorage(app);

// Explicit persistence choice for a staff desk tool (docs/05 session handling). Local
// persistence for normal ergonomics; a shared-machine deployment can switch to session.
void setPersistence(auth, browserLocalPersistence);

let emulatorsConnected = false;
if (useEmulators && !emulatorsConnected) {
  emulatorsConnected = true;
  // Wire all four emulators on the SAME app instance the seam uses; Functions MUST be the
  // regioned instance or connectFunctionsEmulator leaves it pointing at live me-central1.
  connectAuthEmulator(auth, emulatorHosts.auth, { disableWarnings: true });
  connectFirestoreEmulator(db, emulatorHosts.firestore.host, emulatorHosts.firestore.port);
  connectFunctionsEmulator(functions, emulatorHosts.functions.host, emulatorHosts.functions.port);
  connectStorageEmulator(storage, emulatorHosts.storage.host, emulatorHosts.storage.port);
  // eslint-disable-next-line no-console
  console.info('Connected to Firebase emulators (127.0.0.1)');
}
